import { useState, type FormEvent } from 'react';
import { useSearch } from '@tanstack/react-router';

import { api, ApiError } from '../lib/api.js';

export function AcceptInvitePage() {
  const search = useSearch({ strict: false }) as { token?: string };
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!search.token) {
      setError('Missing invitation token');
      return;
    }
    setError(null);
    setPending(true);
    try {
      await api.post('/api/invitations/accept', {
        token: search.token,
        name: name || undefined,
        password: password || undefined,
      });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError) setError(`${err.code}: ${err.message}`);
      else setError(String(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="card w-full max-w-md">
        <h1 className="text-xl font-semibold">Accept invitation</h1>
        {done ? (
          <div className="mt-6 space-y-3">
            <p className="text-sm text-slate-700">
              You're in. Sign in with your email and password.
            </p>
            <a className="btn-primary" href="/login">
              Go to sign in
            </a>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <label className="block">
              <span className="text-sm font-medium">Full name (optional)</span>
              <input
                className="input mt-1"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Choose a password (optional)</span>
              <input
                type="password"
                className="input mt-1"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
              />
              <span className="mt-1 block text-xs text-slate-500">
                Leave blank to receive a one-time password by email.
              </span>
            </label>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button type="submit" className="btn-primary w-full" disabled={pending}>
              {pending ? 'Activating…' : 'Activate account'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
